import React, { useState, useEffect } from 'react';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import AssignWorkerForm from './AssignWorkerForm';
import StepDataForm from './StepDataForm';
import actionLabels from '../../config/actionLabels';
import Modal from '../UI/Modal';
import ProgressBar from '../UI/ProgressBar';

const StepList = ({ steps, phaseIndex, isMaster, isWorker, orderId, departmentId, onUpdate }) => {
	const { user, logout } = useUser();
	const [showHistory, setShowHistory] = useState(false);
	const [selectedStep, setSelectedStep] = useState(null);
	const [shiftSchedules, setShiftSchedules] = useState([]);

	useEffect(() => {
		const fetchShiftSchedules = async () => {
			try {
				const response = await fetchWithAuth(
					`${process.env.REACT_APP_API_URL}/orders/shifts`,
					{},
					logout
				);
				const data = await response.json();
				setShiftSchedules(data);
			} catch (err) {
				console.error("Ошибка загрузки смен:", err);
			}
		};

		fetchShiftSchedules();
	}, [logout]);

	const handleStepStatusChange = async (stepIndex, newStatus) => {
		try {
			await fetchWithAuth(
				`${process.env.REACT_APP_API_URL}/orders/${orderId}/phase/${phaseIndex}/step/${stepIndex}/status`,
				{
					method: 'POST',
					body: JSON.stringify({ status: newStatus }),
				},
				logout
			);
			onUpdate();
		} catch (err) {
			console.error('Ошибка изменения статуса шага:', err);
		}
	};

	const renderWorkerAssignments = (step, stepIndex) => {
		const myAssignments = step.assignees.filter(a => a.userId === user.user.id);
		
		// Группируем назначения по статусу
		const activeAssignments = myAssignments.filter(a => a.status !== 'done');
		const completedAssignments = myAssignments.filter(a => a.status === 'done');

		// Группируем активные назначения по дате и смене
		const groupedActiveAssignments = activeAssignments.reduce((acc, assignee) => {
			const key = `${assignee.date}-${assignee.shiftSchedule}`;
			if (!acc[key]) {
				acc[key] = {
					date: assignee.date,
					shiftSchedule: shiftSchedules.find(shift => shift._id === assignee.shiftSchedule)?.name,
					assignments: []
				};
			}
			acc[key].assignments.push(assignee);
			return acc;
		}, {});

		return (
			<div className="space-y-4">
				{/* Активные назначения */}
				{Object.values(groupedActiveAssignments).length > 0 && (
					<div className="space-y-2">
						<h4 className="text-gray-400 font-medium">Текущие назначения:</h4>
						{Object.values(groupedActiveAssignments).map((group) => (
							<div key={`${group.date}-${group.shiftSchedule}`} className="border rounded p-2 bg-[#2D2D30] border-[#3E3E42]">
								<div className="mb-2">
									<p className="text-gray-400">
										Дата: {new Date(group.date).toLocaleDateString()}
									</p>
									<p className="text-gray-400">
										Смена: {group.shiftSchedule}
									</p>
								</div>
								<div className="space-y-2">
									{group.assignments.map((assignee) => (
										<div key={assignee.userId + assignee.date + assignee.shiftSchedule} className="border rounded p-2 bg-[#1E1E1E] border-[#3E3E42]">
											<div className="flex justify-between items-center mb-2">
												<span className={`px-2 py-1 rounded text-sm ${
													assignee.status === 'done' ? 'bg-green-500' :
													assignee.status === 'in_progress' ? 'bg-yellow-500' :
													'bg-gray-500'
												}`}>
													{actionLabels.status[assignee.status]}
												</span>
											</div>
											<StepDataForm
												orderId={orderId}
												phaseIndex={phaseIndex}
												stepIndex={stepIndex}
												onUpdate={onUpdate}
												assignee={assignee}
												onStatusChange={handleStepStatusChange}
											/>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				)}

				{/* История выполненных назначений */}
				{completedAssignments.length > 0 && (
					<div className="space-y-2">
						<div className="flex justify-between items-center">
							<h4 className="text-gray-400 font-medium">История выполненных назначений:</h4>
							<button
								onClick={() => {
									setSelectedStep(step);
									setShowHistory(true);
								}}
								className="text-blue-500 hover:text-blue-700 text-sm"
							>
								Показать историю
							</button>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderStep = (step, stepIndex) => {
		const shouldShowWorkerForm = isWorker;
		const shouldShowMasterForm = isMaster;

		if (!shouldShowWorkerForm && !shouldShowMasterForm) {
			return null;
		}

		return (
			<div key={stepIndex} className="border rounded p-3 bg-[#1E1E1E] border-[#3E3E42]">
				<div className="flex justify-between items-center mb-2">
					<div>
						<h4 className="font-medium text-white">{step.name}</h4>
						<p className="text-sm text-gray-400">
							{step.roleRequired} • {step.duration} мин
						</p>
					</div>
					<div className="flex items-center gap-2">
						<span className={`px-2 py-1 rounded text-sm ${
							step.status === 'done' ? 'bg-green-500' :
							step.status === 'in_progress' ? 'bg-yellow-500' :
							'bg-gray-500'
						}`}>
							{actionLabels.status[step.status]}
						</span>
						{isMaster && (
							<select
								value={step.status}
								onChange={(e) => handleStepStatusChange(stepIndex, e.target.value)}
								className="bg-[#1E1E1E] border border-[#3E3E42] rounded px-2 py-1 text-sm"
							>
								<option value="pending">Ожидание</option>
								<option value="in_progress">В работе</option>
								<option value="done">Завершено</option>
							</select>
						)}
					</div>
				</div>

				<div className="mb-2">
					<ProgressBar progress={step.progress} />
				</div>

				{shouldShowMasterForm && (
					<AssignWorkerForm
						departmentId={departmentId}
						step={step}
						orderId={orderId}
						phaseIndex={phaseIndex}
						stepIndex={stepIndex}
						onUpdate={onUpdate}
						shiftSchedules={shiftSchedules}
					/>
				)}

				{shouldShowWorkerForm && renderWorkerAssignments(step, stepIndex)}
			</div>
		);
	};

	const renderHistoryModal = () => {
		if (!selectedStep) return null;

		const completedAssignments = selectedStep.assignees
			.filter(a => a.userId === user.user.id && a.status === 'done')
			.sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt));

		return (
			<Modal isOpen={showHistory} onClose={() => setShowHistory(false)}>
				<div className="space-y-4">
					<h3 className="text-lg font-medium text-white">История выполненных назначений</h3>
					<div className="space-y-2">
						{completedAssignments.map((assignee) => (
							<div key={assignee.userId + assignee.date + assignee.shiftSchedule} 
								className="border rounded p-2 bg-[#2D2D30] border-[#3E3E42]">
								<div className="grid grid-cols-2 gap-2 text-sm text-gray-400">
									<div>
										<p>Дата: {new Date(assignee.date).toLocaleDateString()}</p>
										<p>Смена: {shiftSchedules.find(shift => shift._id === assignee.shiftSchedule)?.name}</p>
									</div>
									<div>
										<p>План: {assignee.quantity}</p>
										<p>Годная продукция: {assignee.actualQuantity}</p>
										<p>Брак: {assignee.defectQuantity}</p>
									</div>
								</div>
								<div className="mt-2 text-xs text-gray-500">
									<p>Выполнено: {new Date(assignee.completedAt).toLocaleString()}</p>
								</div>
							</div>
						))}
					</div>
				</div>
			</Modal>
		);
	};

	return (
		<div className="space-y-2">
			{steps.map((step, stepIndex) => renderStep(step, stepIndex))}
			{renderHistoryModal()}
		</div>
	);
};

export default StepList;
  